import {
  Box,
  Heading,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { useGetUserQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AddToSlackButton } from './AddToSlackButton';
import { DeactivateBorrowerSlackInstallationsConfirmation } from './DeactivateBorrowerSlackInstallationsConfirmation';
import { useGetActiveSlackInstallationsQuery } from './slack.gql.generated';
import { SlackInstallationsContainer } from './SlackInstallationsContainer';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { FiSettings } from 'react-icons/fi';
import { errorToastConfig } from '~/packages/common-frontend/constants';

export const SlackInstallationsView = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: slackInstallationsData,
    loading: slackInstallationsLoading,
    refetch: refetchSlackInstallationsData,
  } = useGetActiveSlackInstallationsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  if (
    slackInstallationsLoading ||
    !slackInstallationsData ||
    !slackInstallationsData.activeSlackInstallations ||
    userLoading ||
    !userData ||
    !userData.user
  ) {
    return (
      <Skeleton data-testid="loading-slack-installations" height="600px" />
    );
  }

  return (
    <Box mt="35px" mb="70px">
      <Heading as="h4" size="md" textAlign="left" marginBottom="30px">
        Integrate with Slack
      </Heading>
      <Text width="40%" mt="10px" fontSize="sm" align="left">
        You can integrate Finley with your company&apos;s Slack workspace to
        receive notifications about credit facilities managed by Finley.
      </Text>
      <Text width="40%" mt="10px" fontSize="sm" align="left">
        There is no limit to the number of Slack channels where you can receive
        Finley notifications. For each channel you want to integrate, click on
        the button below and complete the prompts that follow.
      </Text>
      <Text width="40%" mt="10px" fontSize="sm" align="left">
        After integrating Finley with a Slack channel, you must invite Finley to
        the channel in order to finish the set-up process.{' '}
        <strong>
          You can do this by navigating to the channel in Slack and typing{' '}
        </strong>
        <code>/invite @Finley</code>
      </Text>
      <Box height="15px" />
      <AddToSlackButton />
      <Box height="60px" />
      <Flex alignItems="center" mb="35px">
        <Heading as="h4" size="md" textAlign="left">
          Integrated Slack channels
        </Heading>
        <Menu>
          <MenuButton
            as={IconButton}
            bg="transparent"
            icon={<Icon as={FiSettings} />}
          />
          <MenuList>
            <MenuItem
              onClick={() => {
                refetchSlackInstallationsData();
              }}
            >
              Refresh integrated channels
            </MenuItem>
            <MenuItem onClick={onOpen}>
              Deactivate all integrated channels
            </MenuItem>
          </MenuList>
        </Menu>
        <DeactivateBorrowerSlackInstallationsConfirmation
          onClose={onClose}
          isOpen={isOpen}
        />
      </Flex>
      <SlackInstallationsContainer
        slackInstallations={slackInstallationsData.activeSlackInstallations}
        isLoading={slackInstallationsLoading}
        organizationId={userData.user.organizationId}
      />
    </Box>
  );
};
